import clsx from 'clsx';
import Image, { ImageLoaderProps, ImageProps } from 'next/image';
import * as React from 'react';

export type NextImageProps = {
  useSkeleton?: boolean;
  imgClassName?: string;
  loadingClassName?: string;
  alt?: string;
  width?: string | number;
  height?: string | number;
  isFallback?: boolean;
} & ImageProps & {
    baseUrls?: string;
    useCustomLoader?: boolean;
  };

const DEFAULT_ASPECT_RATIO = 4 / 3;

//TODO: Rename baseurls -> baseurl
const customLoader = ({
  src = '',
  width = 1200,
  aspectRatio,
  baseUrls = process.env.IMAGE_BASE_URL ||
    process.env.NEXT_PUBLIC_IMAGE_BASE_URL ||
    '',
  isFallback,
}: ImageLoaderProps & {
  baseUrls?: string;
  aspectRatio: number;
  isFallback?: boolean;
}): string => {
  const verifiySrcPathTrail = src.replace(/^\/+/, '');

  // Return base fallback if no image
  if (isFallback) {
    const baseFallbackUrl =
      process.env.CONTENT_IMAGE_BASE_URL ||
      process.env.NEXT_PUBLIC_CONTENT_IMAGE_BASE_URL ||
      '';

    return `${baseFallbackUrl}/${verifiySrcPathTrail}`;
  }

  // Return image path as it comes for absolute paths
  if (verifiySrcPathTrail.startsWith('http')) {
    return verifiySrcPathTrail;
  }

  //WORKAROUND: BE should fixed that
  const calcHeight = Math.round(width / aspectRatio / 10) * 10;

  return `${baseUrls.concat(
    '/',
    `${width}x${calcHeight}/`,
    verifiySrcPathTrail
  )}`;
};

/**
 *
 * @description Must set width using `w-` className
 * @param useSkeleton add background with pulse animation, don't use it if image is transparent
 */
export default function NextImage({
  useCustomLoader = true,
  useSkeleton = false,
  alt,
  src,
  className,
  imgClassName,
  loadingClassName,
  baseUrls,
  layout = 'responsive',
  width,
  height, // original height
  isFallback,
  objectFit,
  objectPosition,
  style,
  ...rest
}: Omit<NextImageProps, 'alt'> & {
  alt?: string;
  layout?: string;
  objectFit?: any;
  objectPosition?: string;
}) {
  const [status, setStatus] = React.useState(
    useSkeleton ? 'loading' : 'complete'
  );
  const widthIsSet = className?.includes('w-') ?? false;

  const aspectRatio =
    width && height ? Number(width) / Number(height) : DEFAULT_ASPECT_RATIO;

  const css = {
    ...(layout !== 'fill' &&
      layout !== 'fixed' && {
        maxWidth: '100%',
        height: 'auto',
      }),
    ...(objectPosition && { objectPosition }),
    ...(objectFit && { objectFit }),
    ...style,
  };

  const layoutProps = {
    ...((layout === 'responsive' || layout === 'fill') && { sizes: '100vw' }),
    ...(layout === 'fill' && { fill: true }),
  };

  return (
    <figure
      style={!widthIsSet && width ? { width: `${width}px` } : undefined}
      className={className}
    >
      <Image
        {...(useCustomLoader && {
          loader: (loaderProps: ImageLoaderProps) => {
            return customLoader({
              baseUrls,
              aspectRatio,
              isFallback,
              ...loaderProps,
            });
          },
        })}
        className={clsx(
          imgClassName,
          'bg-transparent text-transparent ',
          status === 'loading' && clsx('animate-pulse', loadingClassName)
        )}
        src={src}
        width={width}
        onLoadingComplete={() => setStatus('complete')}
        style={css}
        alt={alt ?? (src as string)}
        {...layoutProps}
        {...rest}
      />
    </figure>
  );
}
